import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/hero-vapour-blasting.jpg";
import heroWebp1280 from "@/assets/hero-vapour-blasting-1280.webp";
import heroWebp768 from "@/assets/hero-vapour-blasting-768.webp";

const HeroSection = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background z-10" />
        <picture>
          <source
            type="image/webp"
            srcSet={`${heroWebp768} 768w, ${heroWebp1280} 1280w`}
            sizes="100vw"
          />
          <img
            src={heroImage}
            alt="Vapour blasted Suzuki GS cylinder head restored by Precision Vapour Blasting Christchurch"
            width={1376}
            height={768}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
      </div>

      {/* Content */}
      <div className="relative z-20 container-wide px-4 md:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="-mt-[12vh] md:-mt-[14vh]">
            <p
              className="text-primary font-medium tracking-[0.3em] uppercase mb-6 animate-fade-in-up opacity-0"
              style={{ animationDelay: "0.2s" }}
            >
              Christchurch, New Zealand
            </p>

            <h1
              className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up opacity-0"
              style={{ animationDelay: "0.4s" }}
            >
              <span className="text-foreground">PRECISION</span>
              <br />
              <span className="gradient-text glow-text">VAPOUR BLASTING</span>
              <br />
              <span className="text-foreground">SURFACE RESTORATION</span>
            </h1>

            <p
              className="text-lg md:text-xl text-foreground/95 max-w-2xl mx-auto animate-fade-in-up opacity-0 inline-block px-6 py-3 rounded-lg bg-background/60 backdrop-blur-sm [text-shadow:0_2px_8px_hsl(var(--background)/0.9)]"
              style={{ animationDelay: "0.6s" }}
            >
              Wet blasting that transforms corroded and worn surfaces into pristine condition. Ideal for automotive,
              industrial and marine applications.
            </p>
          </div>

          <div className="mt-10" />

          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up opacity-0"
            style={{ animationDelay: "0.8s" }}
          >
            <Button variant="hero" size="xl" asChild>
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
            <Button variant="heroOutline" size="xl" asChild>
              <Link to="/about" className="flex items-center gap-2">
                <Play className="w-5 h-5" />
                Learn More
              </Link>
            </Button>
          </div>

          {/* Stats */}
          <div
            className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-border/30 max-w-2xl mx-auto animate-fade-in-up opacity-0"
            style={{ animationDelay: "1s" }}
          >
            <div>
              <p className="font-display text-3xl md:text-4xl font-bold text-primary">Eco</p>
              <p className="text-sm text-muted-foreground mt-1">Friendly Process</p>
            </div>
            <div>
              <p className="font-display text-3xl md:text-4xl font-bold text-primary">Zero</p>
              <p className="text-sm text-muted-foreground mt-1">Surface Damage</p>
            </div>
            <div>
              <p className="font-display text-3xl md:text-4xl font-bold text-primary">Fast</p>
              <p className="text-sm text-muted-foreground mt-1">Turnaround</p>
            </div>
          </div>
        </div>
      </div>

      <Link
        to="/about"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce"
        aria-label="Learn more about us"
      >
        <ChevronDown className="w-8 h-8 text-primary" />
      </Link>
    </section>
  );
};

export default HeroSection;
